import deepMerge from 'lodash.merge';
export const getCumulatedTotal = ({ totals, index }) => totals.reduce((acc, curr, i) => i < index ? acc + curr : acc, 0);
export const getDataFromQueries = ({ queries, offset, limit, ...params }) => {
    const totals = queries.map(({ total }) => total);
    let remainingLimit = limit;
    let currentOffset = offset;
    return queries.map(({ total, queryFn }, index) => {
        const startIndex = getCumulatedTotal({ totals, index });
        const endIndex = getCumulatedTotal({ totals, index: index + 1 });
        // Skip if we've already used up all items from this query
        if (currentOffset >= endIndex) {
            return null;
        }
        // Calculate how many items we need from this query
        const queryOffset = Math.max(0, currentOffset - startIndex);
        const remainingItemsInQuery = total - queryOffset;
        const itemsToFetch = Math.min(remainingLimit, remainingItemsInQuery);
        if (itemsToFetch <= 0 || queryFn == null) {
            return null;
        }
        // Update for next query
        currentOffset = endIndex;
        remainingLimit -= itemsToFetch;
        return queryFn({
            ...params,
            limit: itemsToFetch,
            offset: queryOffset,
        });
    }).filter((item) => Boolean(item));
};
export const makeCombinedPaginatedQuery = () => {
    const totals = new Map();
    const total = new Map();
    const metadata = new Map();
    return ({ queryFns }) => async ({ params, queryKeyHash }) => {
        const { offset } = params;
        if (offset === 0) {
            const res = await Promise.all(queryFns.map(query => query({ ...params, offset: 0, limit: 1 })));
            res.forEach((res) => {
                metadata.set(queryKeyHash, deepMerge({}, metadata.get(queryKeyHash), res.metadata ?? {}));
            });
            totals.set(queryKeyHash, res.map(r => r.total));
            total.set(queryKeyHash, totals.get(queryKeyHash)?.reduce((acc, curr) => acc + curr, 0) ?? 0);
        }
        const queries = queryFns.map((queryFn, i) => ({ queryFn, total: totals.get(queryKeyHash)?.[i] ?? 0 }));
        const data = Promise.all(getDataFromQueries({ queries, ...params }));
        return (await data)
            .reduce((acc, curr) => ({
            ...acc,
            items: [
                ...acc.items,
                ...curr.items.map(item => ({
                    ...item,
                    _collection: curr.collection,
                })),
            ],
        }), { items: [], total: total.get(queryKeyHash) ?? 0, metadata: metadata.get(queryKeyHash) });
    };
};
